import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../common';
import { EdiService, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiEnrollment, EdiEnrollments, EnrollStatus } from '../../../dtos/billing/edi.dto';
import { Cell, ErrorNote, SimulatedBadge, errorText } from './ediUi';

const STATUS_STYLE: Record<EnrollStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  SUBMITTED: 'bg-sky-100 text-sky-800',
  NOT_STARTED: 'bg-slate-100 text-slate-700',
  FAILED: 'bg-red-100 text-red-800',
  EXPIRING: 'bg-amber-100 text-amber-900',
};

const BLOCKS: Record<string, string> = {
  '837': 'Electronic claims (claims go to the keying worksheet until active)',
  '835': 'Electronic remittance posting',
};

const StatusChip: React.FC<{ row?: EdiEnrollment }> = ({ row }) => {
  const status: EnrollStatus = row?.status || 'NOT_STARTED';
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${STATUS_STYLE[status]}`}>
      {status.replace(/_/g, ' ').toLowerCase()}
      {row?.expiresAt && status === 'EXPIRING' ? ` ${formatEdiDate(row.expiresAt)}` : ''}
    </span>
  );
};

/** ENR-01 enrollment status per payer (837 claims, 835 remittance). */
const EnrollmentStatus: React.FC = () => {
  const [data, setData] = useState<EdiEnrollments | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EdiService.listEnrollments()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load enrollment status')); });
    return () => { cancelled = true; };
  }, []);

  if (error) return <div className="p-3"><ErrorNote message={error} /></div>;
  if (!data) return <LoadingSpinner label="Loading enrollment status" />;

  const find = (payerId: string, tx: string) => data.enrollments.find((e) => e.payerId === payerId && e.txType === tx);
  const payerIds = Array.from(new Set([...data.payers.map((p) => p.payerId), ...data.enrollments.map((e) => e.payerId)]));
  const nameOf = (id: string) => data.payers.find((p) => p.payerId === id)?.name || id;

  return (
    <div className="space-y-3 p-3 sm:p-4" aria-label="Enrollment status">
      <h2 className="text-base font-semibold text-slate-800">Payer enrollment</h2>
      {payerIds.length === 0 ? <p className="text-sm text-slate-500">No payers yet.</p> : (
        <div className="text-sm">
          <div className="hidden grid-cols-4 gap-2 border-b border-slate-100 pb-1 text-xs uppercase text-slate-500 sm:grid">
            <span>Payer</span><span>837 claims</span><span>835 remittance</span><span>What it blocks</span>
          </div>
          {payerIds.map((id) => {
            const e837 = find(id, '837');
            const e835 = find(id, '835');
            const blocked = [e837, e835].map((row, i) => (row?.status === 'ACTIVE' ? null : BLOCKS[i === 0 ? '837' : '835'])).filter(Boolean);
            return (
              <div key={id} className="grid grid-cols-1 gap-1 border-b border-slate-100 py-2 sm:grid-cols-4 sm:gap-2" data-testid={`enroll-${id}`}>
                <Cell label="Payer" className="font-medium">{nameOf(id)}{(e837?.simulated || e835?.simulated) && <SimulatedBadge className="ml-1" />}</Cell>
                <Cell label="837"><StatusChip row={e837} /></Cell>
                <Cell label="835"><StatusChip row={e835} /></Cell>
                <Cell label="Blocks" className="text-slate-600">{blocked.length ? blocked.join('; ') : 'Nothing'}</Cell>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnrollmentStatus;
